import { DateTime } from 'luxon';
import { assert } from '@ember/debug';
import BillingCredits from './billing-credits';
import BillingStatus from './billing-status';
import BillingFCPBalance from './billing-fcp-balance';
import { PRICING_MODEL } from '../constants/billing';

// hours in day * minutes in hour * seconds in minute * ms in seconds
const millisecondsInDay = 24 * 60 * 60 * 1000;
const sevenDays = 7 * millisecondsInDay;
const thirtyDays = 30 * millisecondsInDay;

// TODO: Enable either private or static method Babel transpilation.
// renamed this to avoid confusion between isPresent Ember helper
function isPresentAndHasKeys(x) {
  if (typeof x === 'object' && x !== null) return Object.keys(x).length > 0;
  return Boolean(x);
}

function getTimeUntilExpiration(expirationDate) {
  let expiryDate = DateTime.fromJSDate(expirationDate, { zone: 'utc' });
  return expiryDate.diff(DateTime.utc()).toObject();
}

function getTimeSinceActivation(activationDate) {
  let activeFrom = DateTime.fromJSDate(activationDate, { zone: 'utc' });
  return DateTime.utc().diff(activeFrom).toObject();
}

// TODO: Enable either private or static method Babel transpilation.
function isPaymentExpired({ expMonth, expYear }) {
  if (!expMonth || !expYear) {
    assert('Must provide expiry month and year');
  }
  let expiration = getPaymentExpiration({ month: expMonth, year: expYear });
  return DateTime.utc() > expiration;
}

// TODO: Enable either private or static method Babel transpilation.
function getPaymentExpiration({ month, year }) {
  return DateTime.fromObject({ month, year }).toUTC().endOf('month');
}

export default class Billing {
  #billingAccount;
  #onDemandBillingMethodDetails;
  #contractBillingMethodDetails;
  #fcp;
  #runningStatement;
  #latestConsumptionPoolUpdates;

  /**
   * @param {object} [seed={}]
   * @param {object} [seed.billingAccount]
   * @param {object} [seed.onDemandBillingMethodDetails]
   * @param {object} [seed.contractBillingMethodDetails]
   * @param {object} [seed.fcp]
   * @param {object} [seed.runningStatement]
   * @param {object} [seed.transitions]
   * @param {object} [seed.latestConsumptionPoolUpdates]
   */
  constructor({
    billingAccount = null,
    onDemandBillingMethodDetails = null,
    contractBillingMethodDetails = null,
    fcp = null,
    runningStatement = null,
    latestConsumptionPoolUpdates = null,
  } = {}) {
    this.#billingAccount = billingAccount;
    this.#onDemandBillingMethodDetails = onDemandBillingMethodDetails;
    this.#contractBillingMethodDetails = contractBillingMethodDetails;
    this.#runningStatement = runningStatement;
    this.#fcp = fcp;
    this.#latestConsumptionPoolUpdates = latestConsumptionPoolUpdates;
  }

  /** @type {object|null} */
  get billingAccount() {
    return this.#billingAccount;
  }

  /** @type {string} */
  get pricingModel() {
    return this.billingAccount?.pricingModel;
  }

  /** @type {BillingCredits} */
  get credits() {
    // temporarily calculate estimated remaining balance on the front-end while
    // systems team explores options for keeping updated on the backend
    let estimatedRemainingCredits =
      +this.billingAccount?.balance - +this.runningStatement?.total;

    // remaining credits lowest value should only ever be $0
    // if remaining credits is undefined, set it to $0 to ensure UI does not break
    if (!estimatedRemainingCredits || estimatedRemainingCredits < 0) {
      estimatedRemainingCredits = 0;
    }

    return new BillingCredits(estimatedRemainingCredits);
  }

  /** @type {object|null} */
  get cardDetails() {
    return this.#onDemandBillingMethodDetails?.cardDetails || null;
  }

  /** @type {object|null} */
  get billingMethodDetails() {
    return this.#onDemandBillingMethodDetails?.billingMethod || null;
  }

  /** @type {BillingStatus} */
  get status() {
    return new BillingStatus(
      this.billingAccount?.status,
      this.billingAccount?.onDemandStatus,
    );
  }

  /** @type {object|null} */
  get runningStatement() {
    return this.#runningStatement;
  }

  /** @type {object|null} */
  get hasPreviousStatement() {
    return isPresentAndHasKeys(
      this.runningStatement?.statementMetadata?.previousStatement,
    );
  }

  /** @type {object|null} */
  get consumptionPools() {
    return this.#fcp?.consumptionPools || [];
  }

  /** @type {object|null} */
  get consumptionPoolsSummary() {
    return this.#fcp?.summary || null;
  }

  /** @type {object|null} */
  // activeConsumptionPool is the currently active consumption pool.
  // It returns null if the consumption pool is upcoming (and there
  // is not a current active contract) or is expired and there is no renewal
  get activeConsumptionPool() {
    let activeConsumptionPool = this.#fcp?.consumptionPools.find((a) => {
      return a.activeFrom <= DateTime.utc() && a.activeUntil >= DateTime.utc();
    });
    return activeConsumptionPool || null;
  }

  /** @type {object|null} */
  // latestConsumptionPool will return the most recent consumption pool that has already started,
  // including expired contracts if they exist
  get latestConsumptionPool() {
    if (!this.hasConsumptionPayment) return null;

    const now = DateTime.utc();

    return (
      this.#fcp?.consumptionPools
        .filter((a) => a.activeFrom <= now)
        .reduce((a, b) => (a.activeUntil > b.activeUntil ? a : b)) ?? null
    );
  }

  /** @type {number|null} */
  get estimatedRemainingBalance() {
    return this.hasConsumptionPayment
      ? this.fcpBalance.estimatedRemainingBalance
      : null;
  }

  /** @type {BillingFCPBalance} */
  get fcpBalance() {
    return this.hasConsumptionPayment
      ? new BillingFCPBalance(
          this.latestConsumptionPool.initialAmount,
          this.latestConsumptionPool.recognizedBalance,
          this.latestConsumptionPool.runningTotal,
        )
      : this.upcomingConsumptionPool?.initialAmount;
  }

  /** @type {object|null} */
  get upcomingConsumptionPool() {
    let upcomingConsumptionPool = this.#fcp?.consumptionPools?.find((a) => {
      return a.activeFrom > DateTime.utc();
    });

    return upcomingConsumptionPool || null;
  }

  /** @type {boolean} */
  get hasCardError() {
    return this.status?.isDelinquent || this.cardIsExpired;
  }

  /** @type {boolean} */
  get cardIsExpired() {
    return this.cardDetails && isPaymentExpired(this.cardDetails);
  }

  /** @type {boolean} */
  get renewalRecentlyActivated() {
    if (this.activeConsumptionPool) {
      let timeSinceActivation = getTimeSinceActivation(
        this.activeConsumptionPool.activeFrom,
      );

      return (
        this.hasConsumptionPayment &&
        this.consumptionPools.length > 1 &&
        timeSinceActivation.milliseconds <= sevenDays
      );
    }

    return false;
  }

  /** @type {boolean} */
  get contractExpiringSoon() {
    if (this.activeConsumptionPool) {
      let timeUntilExpiration = getTimeUntilExpiration(
        this.activeConsumptionPool.activeUntil,
      );

      return (
        this.hasConsumptionPayment &&
        timeUntilExpiration.milliseconds <= thirtyDays &&
        timeUntilExpiration.milliseconds > 0
      );
    }

    return false;
  }

  /** @type {boolean} */
  get contractExpired() {
    let timeUntilExpiration = getTimeUntilExpiration(
      this.latestConsumptionPool.activeUntil,
    );
    return (
      this.hasConsumptionPayment &&
      this.latestConsumptionPool &&
      timeUntilExpiration.milliseconds <= 0
    );
  }

  /** @type {boolean} */
  get expiredDepletedNoRenewal() {
    return (
      this.contractExpired &&
      !this.upcomingConsumptionPool &&
      this.fcpBalance.estimatedRemainingBalance <= 0
    );
  }

  /** @type {boolean} */
  get hasContractPayment() {
    return this.pricingModel === PRICING_MODEL.ENTITLEMENT;
  }

  // We can return the entitlement contract's details from the
  // contractBillingMethodDetails for now, but will need to
  // consider the concept of latestEntitlementContract,
  // expired, future, etc. once the BE infrastructure supports this
  /** @type {object|null} */
  get entitlementContractDetails() {
    return this.hasContractPayment
      ? this.#contractBillingMethodDetails?.billingMethod
      : null;
  }

  /** @type {boolean} */
  get hasConsumptionPayment() {
    return this.pricingModel === PRICING_MODEL.FLEX;
  }

  /** @type {array|null} */
  get resources() {
    return this.runningStatement?.resources;
  }

  // Both Trial and On Demand accounts will return PRICING_MODEL_PAYG
  // so we also need to check that the On Demand status is not `UNSET`
  // to confirm that it is not a Trial account
  /** @type {boolean} */
  get hasOnDemandPayment() {
    return (
      this.pricingModel === PRICING_MODEL.PAYG && !this.status.isOnDemandUnset
    );
  }

  /** @type {boolean} */
  get hasDiscount() {
    return this.discount > 0;
  }

  /** @type {number} */
  get discount() {
    // if number of credits remaining is greater than the current
    // runningStatement total, we only want to display the discount as
    // that total
    if (this.credits > +this.runningStatement?.total) {
      return this.runningStatement?.total;
    }

    // otherwise, display the full credit balance
    return this.billingAccount?.balance;
  }

  /** @type {number} */
  // returns the calculated total with discount subtracted for SBE
  get totalSBE() {
    if (this.hasDiscount) {
      return +this.runningStatement?.total - +this.discount;
    }

    return +this.runningStatement?.total;
  }

  // Returns updates for the latest consumption pool, which may be active or expired.
  /** @type {Array} */
  get latestConsumptionPoolUpdates() {
    return this.#latestConsumptionPoolUpdates;
  }

  // Sets updates for the latest consumption pool, which may be active or expired.
  /** @param {Array} consumptionPoolUpdates */
  set latestConsumptionPoolUpdates(consumptionPoolUpdates) {
    this.#latestConsumptionPoolUpdates = consumptionPoolUpdates;
  }
}
